import { I18n } from "infrontjs";

import de from "./de.json";
import en from "./en.json";
import ru from "./ru.json";
import cs from "./cs.json";

/**
 * Translation plumbing for the guest-app.
 *
 * The dictionaries are keyed by their GERMAN source text, not by symbolic ids
 * (`"Abbrechen"`, not `"common.cancel"`). Two reasons: the templates stay
 * readable — `<%= _lcs( 'Abbrechen' ) %>` says what it renders — and a missing
 * translation degrades to correct German instead of to a naked key, because
 * IF.I18n returns the key when it finds nothing. `de.json` is therefore an
 * identity map: it is the canonical inventory of every translatable string and
 * the file a translator works from.
 *
 * Templates do not import anything from here — IF.View injects `_lcs` (plus
 * `_lcn` for numbers and `_lcd` for dates) into every render. This module is
 * for the plain JS that renders outside a template: the label maps in
 * core/Region and core/Events, toasts, empty states.
 */

/**
 * Czech plural forms, which IF.I18n does not ship.
 *
 * Its PLURAL_RULES covers en/de/nl/fr/ru/pl/ar and falls back to the English
 * rule (1 vs. everything else) for anything else — which is wrong for Czech,
 * where 2–4 takes its own form ("2 dny", not "2 dnů"). Registering it here, at
 * module scope, means the rule exists before the App constructs its I18n.
 *
 * Russian is already correct in the framework, so it is left alone.
 */
if ( !I18n.PLURAL_RULES.cs )
{
    I18n.PLURAL_RULES.cs = ( n ) =>
    {
        if ( n === 1 ) return "one";
        if ( n >= 2 && n <= 4 ) return "few";
        return "many";
    };
}

/**
 * The languages the guest-app ships. `label` is deliberately written in its own
 * language — a Czech guest scans for "Čeština", not for "Tschechisch".
 */
export const LANGUAGES = [
    { code : "de", label : "Deutsch" },
    { code : "en", label : "English" },
    { code : "ru", label : "Русский" },
    { code : "cs", label : "Čeština" }
];

/** Source language: the one every dictionary key is written in. */
export const DEFAULT_LANGUAGE = "de";

export const DICTIONARIES = { de, en, ru, cs };

// Set by App.initL18n() as soon as the IF.I18n instance exists. Everything
// below degrades to the German key while it is null, which is what the very
// first module-evaluation pass sees.
let instance = null;

export function registerI18n( i18n )
{
    instance = i18n;
}

/**
 * Translate a German source string.
 *
 * @param {string} key       The German text, exactly as it appears in de.json.
 * @param {object} [params]  Interpolation values for `{placeholder}` slots;
 *                           a `count` also selects the plural form.
 * @returns {string}
 */
export function _lcs( key, params = {} )
{
    return instance ? instance.t( key, params ) : key;
}

export function currentLanguage()
{
    return instance ? instance.getCurrentLanguage() : DEFAULT_LANGUAGE;
}

/**
 * Locale tag for Intl (dates, numbers, currency, collation). The bare language
 * code is enough: "de" formats identically to "de-DE", and the app has no
 * region-specific variants. Russian and Czech get their own separators and
 * month names for free.
 */
export function locale()
{
    return currentLanguage();
}

export function isSupportedLanguage( code )
{
    return LANGUAGES.some( ( lang ) => lang.code === code );
}

export function languageLabel( code )
{
    const match = LANGUAGES.find( ( lang ) => lang.code === code );
    return match ? match.label : code;
}

/**
 * Pick a language we actually have a dictionary for: an explicit preference
 * first, then the browser's list, then German.
 *
 * This matters more here than in the admin consoles — a guest arrives by
 * scanning a QR code, on their own phone, in whatever language they set it to.
 * IF.I18n's own detection is not enough: it accepts any valid ISO code, so a
 * French phone would land in "fr" with an empty dictionary.
 */
export function resolveLanguage( preferred = null )
{
    const candidates = [ preferred ];

    if ( typeof navigator !== "undefined" )
    {
        if ( Array.isArray( navigator.languages ) ) candidates.push( ...navigator.languages );
        if ( navigator.language ) candidates.push( navigator.language );
    }

    for ( const candidate of candidates )
    {
        if ( !candidate ) continue;
        const code = String( candidate ).toLowerCase().split( "-" )[ 0 ];
        if ( isSupportedLanguage( code ) ) return code;
    }

    return DEFAULT_LANGUAGE;
}
